import { buildDataTableCols, type DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { buildInputTableCols, type InputTableCol } from '@/components/custom/erp';
import { formatDate, formatNumber } from '@/lib/formatUtils';

type ButToanDieuChinhGiamCongNoRow = MuaHangRecord;

const STATUS_LABELS: Record<string, string> = {
  '0': 'Chưa ghi sổ',
  '3': 'Chờ duyệt',
  '5': 'Đã ghi sổ'
};

const MA_NGV_LABELS: Record<string, string> = {
  '1': '1. Theo hóa đơn',
  '2': '2. Theo khách hàng'
};

export const getDataTableColumns = (handleViewClick: (row: MuaHangRecord) => void): DataTableColumn[] =>
  buildDataTableCols<ButToanDieuChinhGiamCongNoRow>([
    {
      field: 'status',
      headerName: 'Trạng thái',
      width: 120,
      valueGetter: row => row?.status,
      valueFormatter: value => STATUS_LABELS[String(value ?? '')] ?? ''
    },
    {
      field: 'so_ct',
      headerName: 'Số chứng từ',
      width: 150,
      valueGetter: row => row?.so_ct,
      renderCell: params => (
        <button
          onClick={e => {
            e.stopPropagation();
            handleViewClick(params.row);
          }}
          className='hover:text-blue-500 hover:underline'
        >
          {params.row.so_ct}
        </button>
      )
    },
    {
      field: 'ngay_ct',
      headerName: 'Ngày c/từ',
      width: 120,
      valueGetter: row => row?.ngay_ct,
      valueFormatter: value => formatDate(value as MuaHangRecord)
    },
    {
      field: 'ma_kh',
      headerName: 'Mã khách hàng',
      width: 150,
      valueGetter: row => row?.chi_tiet_data?.[0]?.ma_kh_data?.customer_code
    },
    {
      field: 'ten_kh',
      headerName: 'Tên khách hàng',
      width: 200,
      valueGetter: row => row?.chi_tiet_data?.[0]?.ma_kh_data?.customer_name
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải',
      width: 200,
      valueGetter: row => row?.dien_giai
    },
    {
      field: 'tk',
      headerName: 'Tk có',
      width: 150,
      valueGetter: row => row?.tk_data?.code
    },
    {
      field: 't_tien',
      headerName: 'Tổng tiền',
      width: 150,
      columnType: 'number',
      align: 'right',
      valueGetter: row => row?.t_tien,
      valueFormatter: value => formatNumber(value as string | number)
    },
    {
      field: 'ma_nt',
      headerName: 'Ngoại tệ',
      width: 120,
      valueGetter: row => row?.ma_nt_data?.ma_nt
    },
    {
      field: 'ma_ngv',
      headerName: 'Trạng thái',
      width: 120,
      valueGetter: row => row?.ma_ngv,
      valueFormatter: value => MA_NGV_LABELS[String(value ?? '')] ?? '1. Theo khách hàng'
    }
  ]);

const INPUT_TABLE_BASE_COLS: InputTableCol<ButToanDieuChinhGiamCongNoRow>[] = [
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 150,
    renderCell: params => params.row.ma_kh_data?.customer_code
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    renderCell: params => params.row.ma_kh_data?.customer_name
  },
  {
    field: 'id_hd',
    headerName: 'Hóa đơn',
    width: 120,
    renderCell: params => params.row.id_hd_data?.so_ct
  },
  {
    field: 'ngay_ct_hd',
    headerName: 'Ngày hóa đơn',
    width: 120
  },
  {
    field: 'tk_no',
    headerName: 'Tài khoản nợ',
    width: 150,
    renderCell: params => params.row.tk_no_data?.code
  },
  {
    field: 'ten_tk',
    headerName: 'Tên tài khoản',
    width: 200,
    renderCell: params => params.row.tk_no_data?.name
  },
  {
    field: 'ma_nt_hd',
    headerName: 'Ngoại tệ',
    width: 120,
    renderCell: params => params.row.id_hd_data?.ngoai_te
  },
  {
    field: 'ty_gia_hd',
    headerName: 'Tỷ giá hđ',
    width: 120,
    renderCell: () => '1'
  },
  {
    field: 'tien_tren_hd',
    headerName: 'Tiền trên hóa đơn',
    width: 120,
    columnType: 'number',
    align: 'right',
    renderCell: params => formatNumber(params.row.id_hd_data?.tien_tren_hd)
  },
  {
    field: 'tien_con_phai_tt',
    headerName: 'Còn lại',
    width: 120,
    columnType: 'number',
    align: 'right',
    renderCell: params => formatNumber(params.row.id_hd_data?.tien_con_phai_tt)
  },
  {
    field: 'tien_hd_nt',
    headerName: 'Tiền VND',
    width: 120,
    columnType: 'number',
    align: 'right',
    renderCell: params => formatNumber(params.row.id_hd_data?.tien_hd_nt)
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: params => params.row.ma_bp_data?.ma_bp
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: params => params.row.ma_vv_data?.ma_vu_viec
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: params => params.row.ma_hd_data?.ma_hd
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 150,
    renderCell: params => params.row.ma_dtt_data?.ma_dtt
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: params => params.row.ma_ku_data?.ma_ku
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 100,
    renderCell: params => params.row.ma_phi_data?.ma_phi
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: params => params.row.ma_sp_data?.ma_vt
  },
  {
    field: 'lenh_sx',
    headerName: 'Lệnh sản xuất',
    width: 150
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 150,
    renderCell: params => params.row.ma_cp0_data?.ma_cp
  }
];

export const getInputTableColumns = (): DataTableColumn[] => buildInputTableCols(INPUT_TABLE_BASE_COLS);

export const historyColumns: DataTableColumn[] = buildDataTableCols([
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
]);
